import TextInputWithButtons from '$tablet-app/src/app/components/TextInputWithButtons';
import { useRootStackNavigation } from '$tablet-app/src/app/navigation/hooks';
import useBool from '$tablet-app/src/app/utils/state/useBool';
import { append } from '@akin-home/arrays';
import { Button } from '@akin-home/buttons/native';
import {
  CheckListItem,
  ChecklistModel,
  createChecklistItem,
  getChecklistItemDisplayText,
  toggleChecklistItem,
} from '@akin-home/checklists';
import { useUpdateTaskListItem } from '@akin-home/checklists/frontend';
import { Text } from '@akin-home/text/native';
import Checkbox from 'expo-checkbox';
import { StyleProp, ViewStyle } from 'react-native';
import styled from 'styled-components/native';

interface Props {
  checklist: ChecklistModel;
  style?: StyleProp<ViewStyle>;
}

/** Panel for displaying a ChecklistModel */
export const ChecklistPanel = (props: Props) => {
  const { checklist } = props;
  const { id } = checklist;

  const [adding_item, showAddingItem, hideAddingItem] = useBool();

  const update = useUpdateTaskListItem();

  const { navigate } = useRootStackNavigation();

  const onPressItem = (item: CheckListItem) =>
    update({
      id,
      checklist: toggleChecklistItem(checklist.checklist, item.id),
    });

  const onAddItem = (title: string) => {
    hideAddingItem();

    update({
      id,
      checklist: append(checklist.checklist, createChecklistItem({ title })),
    });
  };

  const onPressEdit = () => navigate('EditChecklist', { id });

  return (
    <Container style={props.style as any}>
      <Heading>
        <HeadingText variant="h3" weight="semibold">
          {checklist.title}
        </HeadingText>

        <ChangeButton icon="pencil" variant="text" onPress={onPressEdit}>
          Change
        </ChangeButton>
      </Heading>

      <Checklist>
        {checklist.checklist.map((item) => (
          <ChecklistItemLine key={item.id} item={item} onPress={onPressItem} />
        ))}

        {adding_item ? (
          <TextInputWithButtons
            autoFocus
            onSubmit={onAddItem}
            onCancel={hideAddingItem}
          />
        ) : (
          <Button variant="secondary" size="small" onPress={showAddingItem}>
            Add to list
          </Button>
        )}
      </Checklist>
    </Container>
  );
};

interface ChecklistItemProps {
  item: CheckListItem;
  onPress?(item: CheckListItem): void;
}

const ChecklistItemLine = (props: ChecklistItemProps) => {
  const { item } = props;

  const onPress = () => props.onPress?.(item);

  return (
    <ChecklistItemLineContainer onPress={onPress}>
      <Checkbox value={item.checked} onChange={onPress} />

      <Text variant="h3">{getChecklistItemDisplayText(item)}</Text>
    </ChecklistItemLineContainer>
  );
};

const Container = styled.View`
  border-radius: 15px;
  padding: 20px;
  gap: 20px;
`;

const Heading = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const HeadingText = styled(Text)`
  flex: 1;
`;

const ChangeButton = styled(Button)`
  opacity: 0.5;
`;

const Checklist = styled.View`
  gap: 15px;
  padding-left: 12px;
`;

const ChecklistItemLineContainer = styled.TouchableOpacity`
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;
